import { Injectable } from "@angular/core";
import { EMPTY, from, Observable, tap } from "rxjs";
import { Folder, FolderPathItem, FolderPlanogram } from "src/app/models/folder";
import { DeletePlanogramResult } from "src/app/models/reference-planogram";
import { FolderApiService } from "src/app/services/folder-api.service";
import { PlanogramApiService } from "src/app/services/planogram-api.service";
import { ToastService } from "src/app/services/toasts.service";

/** Owns the home page's folder/planogram data and the HTTP+toast side-effects
 *  for mutating it (create/delete folder, delete/move planogram). The component
 *  reads state via the getters and calls these methods from event handlers; it
 *  no longer duplicates splice + toast logic across four call sites. */
@Injectable()
export class HomePageStore {
  private _rootFolders: Folder[] = [];
  private _displayedFolders: Folder[] = [];
  private _displayedPlanograms: FolderPlanogram[] = [];
  private _currentPath: FolderPathItem[] = [];
  private _isLoading = true;
  /** The folderId the route currently points at (null = root view). Set the
   *  moment navigation begins — before folder contents arrive — so the async
   *  loadRootFolders() can tell "we're on root" apart from "we're navigating
   *  into a folder whose contents haven't loaded yet". _currentPath can't:
   *  it stays stale-empty until applyFolderContents() runs, which let a
   *  fast loadRootFolders() repaint the root view mid-navigation (the flash). */
  private _targetFolderId: string | null = null;

  get rootFolders(): Folder[] { return this._rootFolders; }
  get displayedFolders(): Folder[] { return this._displayedFolders; }
  get displayedPlanograms(): FolderPlanogram[] { return this._displayedPlanograms; }
  get currentPath(): FolderPathItem[] { return this._currentPath; }
  get isLoading(): boolean { return this._isLoading; }
  get currentSelectedFolder(): FolderPathItem | null {
    return this._currentPath.length > 0 ? this._currentPath[this._currentPath.length - 1] : null;
  }

  constructor(
    private folderApi: FolderApiService,
    private planogramApi: PlanogramApiService,
    private toast: ToastService,
  ) {}

  setLoading(loading: boolean): void {
    this._isLoading = loading;
  }

  /** Record the route's navigation target. Pass null for the root view, or the
   *  folderId being navigated into. Must be called before the folder-contents
   *  fetch resolves so loadRootFolders() doesn't repaint root over a pending
   *  folder navigation. */
  setTargetFolder(folderId: string | null): void {
    this._targetFolderId = folderId;
  }

  loadRootFolders(): Observable<Folder[]> {
    return this.folderApi.getUserRootFolders(false).pipe(
      tap({
        next: (folders) => {
          this._rootFolders = folders;
          // Only surface the freshly loaded folders in the sidebar when the
          // route is actually the root view. Gate on _targetFolderId (the
          // route's intended destination) rather than the stale _currentPath,
          // which is still empty mid-navigation into a folder and let this
          // repaint the root view before folder contents arrived — the flash.
          // Copy the array — never alias displayedFolders to rootFolders, or a
          // later deleteFolder splice would mutate both.
          if (this._targetFolderId === null) {
            this._displayedFolders = [...folders];
            this._isLoading = false;
          }
        },
        error: () => {
          this._rootFolders = [];
          if (this._targetFolderId === null) {
            this._displayedFolders = [];
            this._isLoading = false;
          }
        },
      })
    );
  }

  loadFolderContents(folderId: string): Observable<Folder> {
    return this.folderApi.getFolderContents(folderId);
  }

  applyFolderContents(folder: Folder): void {
    // Backend path includes the full hierarchy from root to current folder.
    this._currentPath = folder.path || [];
    this._displayedFolders = folder.subFolders || [];
    this._displayedPlanograms = folder.planograms || [];
    this._isLoading = false;
  }

  resetToRootView(): void {
    this._currentPath = [];
    // Copy — never alias displayedFolders to rootFolders, otherwise later
    // splices via deleteFolder would mutate the shared array.
    this._displayedFolders = [...this._rootFolders];
    this._displayedPlanograms = [];
    this._isLoading = this._rootFolders.length === 0;
  }

  deleteFolder(folder: Folder): Observable<unknown> {
    return this.folderApi.deleteFolder(folder.guid).pipe(
      tap(() => {
        this.toast.show('Folder deleted successfully', {
          classname: 'bg-success text-light',
          delay: 3000,
        });
        this._displayedFolders = this._displayedFolders.filter(f => f.guid !== folder.guid);
        this._rootFolders = this._rootFolders.filter(f => f.guid !== folder.guid);
      })
    );
  }

  deletePlanogram(guid: string, confirm: boolean = false): Observable<DeletePlanogramResult> {
    return from(this.planogramApi.deletePlanogram(guid, confirm)).pipe(
      tap(result => {
        if (result.deleted) {
          this.toast.show('Planogram deleted successfully', {
            classname: 'bg-success text-light',
            delay: 3000,
          });
          this._displayedPlanograms = this._displayedPlanograms.filter(p => p.guid !== guid);
        }
      })
    );
  }

  unlinkPlanogram(guid: string): Observable<FolderPlanogram> {
    return from(this.planogramApi.unlinkPlanogram(guid));
  }

  createFolderInCurrent(name: string): Observable<Folder> {
    const parent = this.currentSelectedFolder;
    if (!parent) return EMPTY;
    return this.folderApi.createFolder(parent.guid, name).pipe(
      tap(folder => {
        this.toast.show('Folder created successfully', {
          classname: 'bg-success text-light',
          delay: 3000,
        });
        this._displayedFolders = [...this._displayedFolders, folder];
      })
    );
  }

  movePlanograms(destinationId: string, guids: string[]): Observable<unknown> {
    return this.folderApi.movePlanograms(destinationId, guids).pipe(
      tap(() => {
        this.toast.show('Planograms moved successfully!', {
          classname: 'bg-success text-light',
          delay: 3000,
        });
        const moved = new Set(guids);
        this._displayedPlanograms = this._displayedPlanograms.filter(p => !moved.has(p.guid));
      })
    );
  }
}
